import React from "react";
import { Box } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { color, font, motion } from "../../design/tokens";

/**
 * The site's only button.
 *
 * Renders as a react-router <Link> for internal `to`, an <a> for external or
 * tel:/mailto: `href`, and a real <button> otherwise — so keyboard behaviour
 * and screen-reader semantics follow what the control actually does rather
 * than how it looks.
 *
 * Square corners, uppercase tracked label, and a fill that wipes in from the
 * bottom on hover. No shadows: depth in this design comes from ink against
 * paper, never from elevation.
 */

const VARIANTS = {
  // Solid ink on paper — primary CTA in light sections.
  primary: {
    rest: { backgroundColor: color.ink, color: color.paper, borderColor: color.ink },
    hover: { backgroundColor: color.clay, color: color.paper, borderColor: color.clay },
  },
  // Outline on paper — secondary CTA in light sections.
  secondary: {
    rest: { backgroundColor: "transparent", color: color.ink, borderColor: color.line },
    hover: { backgroundColor: color.ink, color: color.paper, borderColor: color.ink },
  },
  // Solid paper on ink — primary CTA inside dark sections.
  onInk: {
    rest: { backgroundColor: color.paper, color: color.ink, borderColor: color.paper },
    hover: { backgroundColor: color.clayLight, color: color.ink, borderColor: color.clayLight },
  },
  // Outline on ink — secondary CTA inside dark sections.
  onInkGhost: {
    rest: { backgroundColor: "transparent", color: color.onInk, borderColor: color.lineOnInk },
    hover: { backgroundColor: color.onInk, color: color.ink, borderColor: color.onInk },
  },
};

const SIZES = {
  md: { minHeight: 52, paddingInline: "28px", fontSize: "0.8125rem" },
  lg: { minHeight: 60, paddingInline: "36px", fontSize: "0.875rem" },
  sm: { minHeight: 44, paddingInline: "20px", fontSize: "0.75rem" },
};

const Button = ({
  children,
  to,
  href,
  variant = "primary",
  size = "md",
  fullWidth = false,
  type: buttonType = "button",
  sx,
  ...rest
}) => {
  const palette = VARIANTS[variant] || VARIANTS.primary;
  const dimensions = SIZES[size] || SIZES.md;

  const elementProps = to
    ? { component: RouterLink, to }
    : href
      ? {
          component: "a",
          href,
          // Only true external links get the new-tab treatment; tel:/mailto:
          // must open in place or mobile handoff breaks.
          ...(href.startsWith("http")
            ? { target: "_blank", rel: "noopener noreferrer" }
            : {}),
        }
      : { component: "button", type: buttonType };

  return (
    <Box
      {...elementProps}
      sx={{
        // Reset, since this may render as a <button>.
        appearance: "none",
        border: "1px solid",
        borderRadius: 0,
        cursor: "pointer",
        fontFamily: font.body,
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        textDecoration: "none",
        lineHeight: 1,

        display: fullWidth ? "flex" : "inline-flex",
        width: fullWidth ? "100%" : "auto",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.25,
        textAlign: "center",
        ...dimensions,
        ...palette.rest,

        transition: `background-color ${motion.base} ${motion.ease}, color ${motion.base} ${motion.ease}, border-color ${motion.base} ${motion.ease}`,

        "&:hover": palette.hover,
        "&:disabled": { opacity: 0.45, cursor: "not-allowed", ...palette.rest },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

/**
 * Inline text link with a trailing arrow and an underline that draws in on
 * hover. Used for "read more" style navigation, where a full button would be
 * too heavy.
 */
export const ArrowLink = ({ children, to, href, tone = "ink", sx, ...rest }) => {
  const tones = {
    ink: { color: color.ink, rule: color.ink },
    clay: { color: color.clay, rule: color.clay },
    onInk: { color: color.onInk, rule: color.onInk },
  };
  const palette = tones[tone] || tones.ink;

  const elementProps = to
    ? { component: RouterLink, to }
    : {
        component: "a",
        href,
        ...(href && href.startsWith("http")
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {}),
      };

  return (
    <Box
      {...elementProps}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        fontFamily: font.body,
        fontWeight: 700,
        fontSize: "0.8125rem",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: palette.color,
        // Tall enough to clear the WCAG 2.2 24px minimum on its own, since
        // these are standalone navigation links rather than links inside a
        // sentence (which the success criterion exempts).
        minHeight: 28,
        paddingBottom: "3px",
        // Animated underline via background-size so it doesn't reflow text.
        backgroundImage: `linear-gradient(${palette.rule}, ${palette.rule})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "0 100%",
        backgroundSize: "0% 1px",
        transition: `background-size ${motion.base} ${motion.ease}, opacity ${motion.base} ${motion.ease}`,
        "&:hover": { backgroundSize: "100% 1px" },
        "& svg": { transition: `transform ${motion.base} ${motion.ease}` },
        "&:hover svg": { transform: "translateX(4px)" },
        ...sx,
      }}
      {...rest}
    >
      {children}
      <Box
        component="svg"
        aria-hidden="true"
        viewBox="0 0 16 16"
        sx={{ width: 14, height: 14, fill: "none", stroke: "currentColor", strokeWidth: 1.5 }}
      >
        <path d="M1 8h13M9 3l5 5-5 5" />
      </Box>
    </Box>
  );
};

export default Button;
